import Button from '@components/Button';
import { Collapse } from '@components/Collapse';
import ColorPicker from '@components/ColorPicker';
import { Dropdown } from '@components/Dropdown';
import Image from '@components/Image';
import ImageContainer from '@components/ImageContainer';
import Slider from '@components/Slider';
import Tabbar from '@components/Tabbar';
import Tip from '@components/Tip';
import { selectLibSandtableMarkerIcons } from '@modules/mediaLib';
import {
  $GateMapMarkerActive,
  $editingMapMarkerApi,
} from '@stores/editingMarkerStores';
import {
  ImageType,
  type STMapMarker,
  type STMapMarkerPoint,
  STMapMarkerType,
} from '@types';
import { useGate } from 'effector-react';
import type { Draft } from 'immer';
import { toPercent } from 'pano-utils';
import { useEffect, useState } from 'react';
import { stEditorAmap } from '../STAMap';
import { TitleSetting } from '../components/TitleSetting';
import { MARKER_ICON_DEFAULT } from '../constants';
import { markerAnchorItems, markerTypeItems } from './MarkerSettingsScene';

interface Props {
  marker: STMapMarkerPoint;
}

const valToString = (val: number) => toPercent(val, 0);

const DEFAULT_ICON = '/hotspots/system/map/point/a1.png';

export function FeatureSettingsPoint({ marker }: Props) {
  const { icon, data, showTitle, title } = marker;

  const [isActive, setIsActive] = useState(false);
  useGate($GateMapMarkerActive, isActive);

  const markerType = icon.type ?? 0;

  const updateMarkerObject = (markerData: STMapMarkerPoint) => {
    const markerObject = stEditorAmap.getMarkerObjectById(
      marker.id
    ) as AMap.Marker | null;
    if (!markerObject) return;
    markerObject.setContent(
      stEditorAmap.generateMarkerIcon(markerData.icon, {
        isActive: $GateMapMarkerActive.state.getState(),
        isSelected: true,
      })
    );

    const anchor =
      markerData.icon.type === 1
        ? markerData.icon.mediaIcon?.anchor ?? 'bottom-center'
        : 'center';
    markerObject.setAnchor(anchor);

    // // 解决高德地图位置偏移延迟生效的问题
    // markerObject.setLabel(
    //   stEditorAmap.generateMarkerLabel(
    //     { ...markerData, title: `${markerData.title}\u200C` },
    //     { isSelected: true }
    //   )
    // );
    setTimeout(() => {
      markerObject.setLabel(
        stEditorAmap.generateMarkerLabel(markerData, { isSelected: true })
      );
    });
  };

  useEffect(() => {
    updateMarkerObject(marker);
  }, [isActive, marker]);

  useEffect(() => {
    stEditorAmap.updateRadarSize(icon.scaleX);
  }, [icon.scaleX]);

  const updateMarker = (
    updater: (draft: Draft<STMapMarkerPoint>) => STMapMarker | void
  ) => {
    $editingMapMarkerApi.update((draft) => {
      draft.type ??= STMapMarkerType.point;
      if (draft.type !== STMapMarkerType.point) return;
      updater(draft);
    });
  };

  const handleSelectImages = async () => {
    const { libItems, sysItems } = await selectLibSandtableMarkerIcons();
    if (libItems) {
      updateMarker((draft) => {
        draft.icon.mediaIcon = {
          type: 1,
          id: libItems[0].id,
          url: libItems[0].url,
          anchor: draft.icon.mediaIcon?.anchor ?? 'bottom-center',
        };
      });
    } else if (sysItems) {
      updateMarker((draft) => {
        draft.icon.mediaIcon = {
          type: 0,
          id: 0,
          url: sysItems[0].url,
          anchor: draft.icon.mediaIcon?.anchor ?? 'bottom-center',
        };
      });
    }
  };

  return (
    <>
      <Collapse title="标记图标">
        <Dropdown
          items={markerTypeItems}
          selected={markerType}
          className="mb-20"
          onChange={(val) => {
            updateMarker((draft) => {
              draft.icon.type = val;
              if (val === 1) {
                draft.icon.mediaIcon = {
                  type: 0,
                  id: 0,
                  url: DEFAULT_ICON,
                  anchor: 'bottom-center',
                };
              } else {
                draft.icon.mediaIcon = undefined;
              }
            });
          }}
        />
        {markerType === 0 && (
          <>
            <Tabbar
              tabs={['默认状态', '选中状态']}
              activeIndex={isActive ? 1 : 0}
              onChange={(index) => {
                setIsActive(index === 1);
              }}
            />
            <ColorPicker
              className="mt-20"
              colorString={isActive ? icon.activeFillColor : icon.fillColor}
              onChange={(val) => {
                if (isActive) {
                  updateMarker((draft) => {
                    draft.icon.activeFillColor = val;
                  });
                } else {
                  updateMarker((draft) => {
                    draft.icon.fillColor = val;
                  });
                }
              }}
            >
              <ColorPicker.Header>
                <div>填充颜色：</div>
                <ColorPicker.Reset
                  defaultColor={
                    isActive
                      ? MARKER_ICON_DEFAULT.activeFillColor
                      : MARKER_ICON_DEFAULT.fillColor
                  }
                />
              </ColorPicker.Header>
              <ColorPicker.Bar />
            </ColorPicker>
            <ColorPicker
              className="mt-20"
              colorString={isActive ? icon.activeBorderColor : icon.borderColor}
              onChange={(val) => {
                if (isActive) {
                  updateMarker((draft) => {
                    draft.icon.activeBorderColor = val;
                  });
                } else {
                  updateMarker((draft) => {
                    draft.icon.borderColor = val;
                  });
                }
              }}
            >
              <ColorPicker.Header>
                <div>边框颜色：</div>
                <ColorPicker.Reset
                  defaultColor={
                    isActive
                      ? MARKER_ICON_DEFAULT.activeBorderColor
                      : MARKER_ICON_DEFAULT.borderColor
                  }
                />
              </ColorPicker.Header>
              <ColorPicker.Bar />
            </ColorPicker>
          </>
        )}
        {markerType === 1 && (
          <>
            <ImageContainer
              className="mt-10 h-70"
              onDeleteClick={() => {
                updateMarker((draft) => {
                  if (draft.icon.mediaIcon) {
                    draft.icon.mediaIcon.id = 0;
                    draft.icon.mediaIcon.type = 0;
                    draft.icon.mediaIcon.url = DEFAULT_ICON;
                  }
                });
              }}
            >
              {icon.mediaIcon && (
                <Image
                  src={icon.mediaIcon.url}
                  type={
                    icon.mediaIcon.type ? ImageType.media : ImageType.sysMedia
                  }
                  className="max-w-40 max-h-40"
                />
              )}
            </ImageContainer>
            <div className="mt-10 flex items-center justify-between">
              <Tip>建议尺寸:100*100</Tip>
              <Button size="sm" onClick={handleSelectImages}>
                选择图片
              </Button>
            </div>
          </>
        )}
        <Slider
          className="mt-20"
          min={0.5}
          max={2}
          value={icon.scaleX}
          valToString={valToString}
          onChange={(val) => {
            updateMarker((draft) => {
              draft.icon.scaleX = val;
              draft.icon.scaleY = val;
            });
          }}
        >
          <Slider.Header title="图标缩放：" />
          <Slider.Bar step={0.01} />
          <Slider.Control step={0.01} />
        </Slider>
        {markerType === 1 && (
          <>
            <div className="mt-20">图标锚点</div>
            <Dropdown
              className="mt-10"
              items={markerAnchorItems}
              selected={icon.mediaIcon?.anchor ?? 'bottom-center'}
              onChange={(val) => {
                updateMarker((draft) => {
                  if (draft.icon.mediaIcon) {
                    draft.icon.mediaIcon.anchor = val;
                  }
                });
              }}
            />
          </>
        )}
      </Collapse>
      <TitleSetting
        title={title}
        showTitle={showTitle}
        onTitleChange={(val) => {
          updateMarker((draft) => {
            draft.title = val;
          });
        }}
        onToggleChange={(val) => {
          updateMarker((draft) => {
            draft.showTitle = val;
          });
        }}
      />
    </>
  );
}
